import { Rate } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";

import Review, { ItemReview } from "./Components/Comments/Review";

import CommentsModal from "./Components/Comments";
import styles from "./index.module.css";
import { useRequest } from "ahooks";
import { deleteProductReview, getProductReviews } from "helpers/backend_helper";
import { AdminContext } from "context/adminContext";
import { NotificationContext } from "context/notificationContext";
import { CustomButton } from "Components/CustomButton";

type Props = { itemId: string; reviewCount: number; onReviewChangeCount: (n: number) => void; rating: number };
const ItemReviews = ({ itemId, reviewCount, onReviewChangeCount, rating }: Props) => {
  const [isProductReviewModalOpen, setIsProductReviewModalOpen] = useState(Boolean);
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const { localCountry } = useContext(AdminContext);
  const [reviews, setReviews] = useState<ItemReview[]>([]);
  const [cursorReview, setCursorReview] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [showLast, setShowLast] = useState<ItemReview[]>([]);

  const { run: onDeleteReview } = useRequest((id: string) => deleteProductReview(+id, localCountry.iso.toUpperCase()), {
    ready: !!localCountry?.iso,
    refreshDeps: [localCountry, itemId],
    manual: true,
    onError: (error: any) => {
      openNotificationWithIcon("error", error.response?.data?.message || "something went wrong");
    },
    onSuccess: (_, params) => {
      setReviews((prev) => prev.filter((v) => v.id !== params[0]));
      setShowLast((prev) => prev.filter((v) => v.id !== params[0]));
      onReviewChangeCount(reviewCount - 1);

      if (hasMore) {
        getReviews();
      }
    },
  });

  const { run: getReviews } = useRequest(
    () => getProductReviews(itemId, cursorReview, localCountry.iso.toUpperCase()),
    {
      ready: !!localCountry?.iso,
      refreshDeps: [localCountry, itemId, cursorReview],
      manual: true,
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || "something went wrong");
      },
      onSuccess: (data) => {
        if (!data.reviews.length) {
          setHasMore(false);
        }
        if (reviews.length) {
          setShowLast([...showLast, ...data.reviews]);
          setReviews([...reviews, ...data.reviews]);
        } else {
          setShowLast(data.reviews);
          setReviews(data.reviews);
        }
        setCursorReview(data.cursor);
      },
    },
  );

  useEffect(() => {
    getReviews();

    return () => {
      setReviews([]);
      setShowLast([]);
    };
  }, []);

  const loadMore = useCallback(() => getReviews(), [getReviews]);

  return (
    <div className={styles.container}>
      <div className={styles.nav}>
        <div className={styles.watchAllWrapper}>
          <div>
            <Rate disabled defaultValue={5} className={styles.rateComponent} count={1} />
            <span>{rating ? rating.toFixed(1) : "0.0"}</span>
          </div>
          <div className={styles.elipse} />
          <div>
            <span>{reviewCount}</span>
            <span>Reviews</span>
          </div>
          <div className={styles.elipse} />
          <div style={{ color: "#888888" }}>
            <span>25</span> <span>Rentals</span>
          </div>
          <div className={styles.elipse} />
          <CustomButton
            sizeButton="medium"
            typeButton="grayTextType"
            type="text"
            onClick={() => setIsProductReviewModalOpen(true)}
            style={{ padding: 0 }}
          >
            Watch all
          </CustomButton>
        </div>
      </div>
      <div className={styles.list}>
        {showLast.slice(0, 4).map((item) => (
          <div key={item.id} className={styles.reviewWrapper}>
            <Review item={item} onDelete={onDeleteReview} />
          </div>
        ))}
      </div>
      <CommentsModal
        hasMore={hasMore}
        loadMore={loadMore}
        isModalOpen={isProductReviewModalOpen}
        setIsModalOpen={setIsProductReviewModalOpen}
        reviews={reviews}
        reviewCount={reviewCount}
        onDelete={onDeleteReview}
      />
    </div>
  );
};

export default ItemReviews;
